/**
 * Global keyboard shortcut to toggle the ChatDock (Cmd/Ctrl + /).
 *
 * Browser-safe combo (NOT Ctrl+J — that is the Downloads shortcut on
 * Windows/Linux). No-op on /chat routes (the dock is excluded there), when no
 * surface is active, and when typing in an editable element.
 */
import { useEffect } from 'react';

import { selectActiveDescriptor, useChatDockRegistry } from './useChatDockRegistry';
import { useChatDockStore } from './useChatDockStore';

/** True when the event target is a text input, textarea, select, or contenteditable. */
function isEditableTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
}

/**
 * Registers the global toggle shortcut. Disabled on `/chat` (the dock is excluded
 * there); elsewhere it resolves the same surface as the dock body's
 * `useActiveDock` and the openers (`ChatEdgeHandle`, `GlobalChatToggle`).
 *
 * @param pathname - the current route path (from `useLocation`).
 */
export function useChatDockShortcut(pathname: string): void {
  const onChatRoute = pathname.startsWith('/chat');
  useEffect(() => {
    if (onChatRoute) return undefined;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        if (isEditableTarget(e.target)) return;
        if (!selectActiveDescriptor(useChatDockRegistry.getState())) return;
        e.preventDefault();
        useChatDockStore.getState().toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onChatRoute]);
}
